import { FaIcon, NonIdealState } from '@acp/core-ui';
import {
    Button, HStack, Text, VStack,
} from '@chakra-ui/react';
import {
    faAt, faCheckCircle, faExclamationCircle, faLockKeyholeOpen, faPhone,
} from '@fortawesome/pro-light-svg-icons';
import { useMemo } from 'react';
import { AccessIssuesEntryMethodDefinitions } from '@acp/shared';
import { AccessIssuesWizardMachineContext } from '../machine/context';
import { useEntryMethodDetails, useNextProblemSet } from '../hooks';
import { AccessIssuesWizardEvents } from '../machine/machine';

function AccessIssuesWizardNoSolutionsWindow() {
    const machine = AccessIssuesWizardMachineContext.useActorRef();
    const nextProblemSet = useNextProblemSet();

    return (
        <VStack minH="96" justifyContent="center" borderWidth={1} borderColor="muted.300" rounded="md">
            <NonIdealState
                iconComponent={<FaIcon w={12} h={12} icon={faExclamationCircle} color="danger.500" />}
                title="Please contact Detector Inspector"
                description="Due to the complexity of the Access Issues facing this property, we'll need to connect you with a specialist in our Help team. Please contact them using the details below and quote the property address."
                titleFontSize="lg"
            >
                <VStack gap={4}>
                    <VStack gap={1}>
                        <Text fontSize="small">
                            <FaIcon icon={faPhone} mr={1} />
                            1300 134 563
                        </Text>
                        <Text fontSize="small">
                            <FaIcon icon={faAt} mr={1} />
                            help@detectorinspector.com.au
                        </Text>
                    </VStack>
                    <HStack>
                        {
                            nextProblemSet === null && (
                                <Button
                                    colorScheme="blackAlpha"
                                    bgColor="black"
                                    size="md"
                                    onClick={() => machine.send({ type: 'FORCE_CLOSE_MODAL' })}
                                >
                                    Finish
                                </Button>
                            )
                        }
                        {
                            nextProblemSet !== null && (
                                <Button
                                    colorScheme="blackAlpha"
                                    bgColor="black"
                                    size="md"
                                    onClick={() => machine.send({ type: 'NEXT_PROBLEM_SET' })}
                                >
                                    Next access issue
                                </Button>
                            )
                        }
                    </HStack>
                </VStack>
            </NonIdealState>
        </VStack>
    );
}

type ExtraActions = {
    showExtraActions: false;
    actionText: null;
    action: null;
} | {
    showExtraActions: true;
    actionText: string;
    action: AccessIssuesWizardEvents;
};

function useExtraActions() {
    const availableEntryMethods = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.entryMethods.available
    );

    const blockedEntryMethods = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.blockedEntryMethodQueues
    );

    const extraActions = useMemo<ExtraActions>(() => {
        if (availableEntryMethods.length === 0) {
            return {
                showExtraActions: false,
                actionText: null,
                action: null,
            };
        }

        if (availableEntryMethods.length > 1) {
            return {
                showExtraActions: true,
                actionText: 'Unblock another entry method',
                action: { type: 'UNBLOCK_OTHER_ENTRY_METHOD' },
            };
        }

        const entryMethod = blockedEntryMethods[availableEntryMethods[0]];

        if (entryMethod.is_blocked_by_preferences) {
            return {
                showExtraActions: true,
                actionText: `Enable ${AccessIssuesEntryMethodDefinitions[availableEntryMethods[0]].plural.toLocaleLowerCase()}`,
                action: { type: 'SELECT_ENTRY_METHOD', entryMethod: availableEntryMethods[0] },
            };
        }

        return {
            showExtraActions: true,
            actionText: `Unblock ${AccessIssuesEntryMethodDefinitions[availableEntryMethods[0]].plural.toLocaleLowerCase()}`,
            action: { type: 'SELECT_ENTRY_METHOD', entryMethod: availableEntryMethods[0] },
        };
    }, [availableEntryMethods, blockedEntryMethods]);

    return extraActions;
}

function AccessIssuesWizardResolvedWindow() {
    const machine = AccessIssuesWizardMachineContext.useActorRef();

    const entryMethod = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.entryMethods.current
    );

    const nextProblemSet = useNextProblemSet();
    const entryMethodDefinition = useEntryMethodDetails(entryMethod!);
    const extraActions = useExtraActions();

    if (nextProblemSet !== null) {
        return (
            <VStack minH="96" justifyContent="center">
                <NonIdealState
                    iconComponent={<FaIcon w={12} h={12} icon={faCheckCircle} color="success.500" />}
                    title="Click next to resolve the next issue:"
                    description={nextProblemSet.public_name}
                    titleFontSize="lg"
                >
                    <Button
                        colorScheme="blackAlpha"
                        bgColor="black"
                        size="md"
                        onClick={() => machine.send({ type: 'NEXT_PROBLEM_SET' })}
                    >
                        Next access issue
                    </Button>
                </NonIdealState>
            </VStack>
        );
    }

    return (
        <VStack minH="96" justifyContent="center">
            <NonIdealState
                iconComponent={<FaIcon w={12} h={12} icon={faLockKeyholeOpen} color="success.500" />}
                title={`Access has been arranged via ${entryMethodDefinition.plural.toLocaleLowerCase()}`}
                description="You have resolved all access issues for this entry method."
                titleFontSize="lg"
            >
                <VStack gap={4}>
                    {
                        extraActions.showExtraActions && (
                            <Text textAlign="center">
                                While we&apos;ve got you, to further increase the accessibility
                                of this property we recommend that you also
                                {' '}
                                {
                                    extraActions.actionText.toLocaleLowerCase()
                                }
                                .
                            </Text>
                        )
                    }
                    <HStack>
                        {
                            extraActions.showExtraActions && (
                                <Button
                                    colorScheme="blackAlpha"
                                    bgColor="black"
                                    size="md"
                                    onClick={() => machine.send(extraActions.action)}
                                >
                                    {extraActions.actionText}
                                </Button>
                            )
                        }
                        <Button
                            colorScheme="blackAlpha"
                            bgColor="black"
                            size="md"
                            onClick={() => machine.send({ type: 'CLOSE_MODAL' })}
                        >
                            Finish
                        </Button>
                    </HStack>
                </VStack>

            </NonIdealState>
        </VStack>
    );
}

export { AccessIssuesWizardResolvedWindow, AccessIssuesWizardNoSolutionsWindow };
